import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { theme, responsiveContainer, createGradient } from '../styles/theme';

function Homepage({ onGetStarted }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handlePlanClick = (planName) => {
    if (planName === 'Agency') {
      // Agency plan goes to contact page
      navigate('/contact');
      return;
    }

    if (planName === 'Free') {
      // Free plan - just need to sign up
      if (user) {
        // Already logged in, go to dashboard
        navigate('/dashboard');
      } else {
        // Need to sign up
        navigate('/signup');
      }
      return;
    }

    // Paid plans (Premium, Growth) - need auth then Stripe
    if (user) {
      // Already logged in, go directly to subscription page with selected plan
      navigate(`/dashboard/subscription?plan=${planName.toLowerCase()}`);
    } else {
      // Need to authenticate first, then will redirect to billing
      navigate(`/signup?plan=${planName.toLowerCase()}`);
    }
  };
  const styles = {
    container: {
      backgroundColor: theme.colors.background,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace", // Retro monospace
      minHeight: '100vh',
    },
    
    // Hero Section with Gradient Background
    hero: {
      background: createGradient('#007acc', '#1a8cdd', '135deg').background,
      color: '#fff',
      padding: '120px 20px 80px 20px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    
    // Animated background elements
    heroPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Crect x='11' y='11' width='4' height='4'/%3E%3Crect x='51' y='11' width='4' height='4'/%3E%3Crect x='11' y='51' width='4' height='4'/%3E%3Crect x='51' y='51' width='4' height='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    },
    
    heroContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    heroTitle: {
      fontSize: theme.typography.fontSizes['6xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      marginBottom: theme.spacing.lg,
      lineHeight: '1.1',
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    
    heroSubtitle: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.normal,
      marginBottom: theme.spacing['2xl'],
      maxWidth: '800px',
      margin: `0 auto ${theme.spacing['2xl']} auto`,
      lineHeight: '1.6',
      opacity: 0.95,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    
    heroButtons: {
      display: 'flex',
      gap: theme.spacing.lg,
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    
    primaryButton: {
      backgroundColor: '#fff',
      color: theme.colors.primary,
      border: `2px solid #fff`,
      borderRadius: theme.borderRadius.md,
      padding: '18px 40px',
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    secondaryButton: {
      backgroundColor: 'transparent',
      color: '#fff',
      border: '2px solid rgba(255,255,255,0.8)',
      borderRadius: theme.borderRadius.md,
      padding: '16px 38px',
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    // Features Section with Cards
    features: {
      backgroundColor: theme.colors.surface,
      padding: '100px 20px',
      position: 'relative',
    },
    
    featuresContent: {
      ...responsiveContainer,
    },
    
    sectionTitle: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: theme.spacing['3xl'],
      position: 'relative',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    sectionTitleUnderline: {
      content: '""',
      position: 'absolute',
      bottom: '-10px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '80px',
      height: '4px',
      backgroundColor: theme.colors.primary,
      borderRadius: theme.borderRadius.full,
    },
    
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: theme.spacing['2xl'],
    },
    
    featureCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing['2xl'],
      textAlign: 'center',
      border: `2px solid ${theme.colors.borderDark}`,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    
    featureCardHover: {
      transform: 'translateY(-8px)',
      boxShadow: theme.shadows.xl,
    },
    
    featureIcon: {
      fontSize: theme.typography.fontSizes['5xl'],
      marginBottom: theme.spacing.lg,
      color: theme.colors.primary,
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textShadow: `0 0 20px ${theme.colors.primary}40`,
    },
    
    featureTitle: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    
    featureDescription: {
      fontSize: theme.typography.fontSizes.lg,
      color: theme.colors.textLight,
      lineHeight: '1.6',
    },

    // Stats Section with Animated Numbers
    stats: {
      backgroundColor: theme.colors.secondary,
      color: '#fff',
      padding: '80px 20px',
      textAlign: 'center',
      position: 'relative',
    },
    
    statsOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: createGradient('rgba(0,122,204,0.1)', 'rgba(26,140,221,0.1)').background,
    },
    
    statsContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    statsTitle: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.bold,
      marginBottom: theme.spacing['2xl'],
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: theme.spacing['2xl'],
    },
    
    statItem: {
      textAlign: 'center',
    },
    
    statNumber: {
      fontSize: theme.typography.fontSizes['5xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      color: theme.colors.primary,
      marginBottom: theme.spacing.sm,
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    
    statLabel: {
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.medium,
      opacity: 0.9,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },

    // Enhanced Pricing Section
    pricing: {
      backgroundColor: theme.colors.background,
      padding: '100px 20px',
    },
    
    pricingContent: {
      ...responsiveContainer,
    },
    
    pricingGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: theme.spacing.lg,
      marginTop: theme.spacing['2xl'],
    },
    
    pricingCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing['2xl'],
      textAlign: 'center',
      position: 'relative',
      border: `2px solid ${theme.colors.borderDark}`,
      transition: 'all 0.3s ease',
    },
    
    popularCard: {
      border: `2px solid ${theme.colors.primary}`,
      transform: 'scale(1.05)',
      backgroundColor: theme.colors.surface,
    },
    
    popularBadge: {
      position: 'absolute',
      top: '-15px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: theme.colors.primary,
      color: '#fff',
      padding: '8px 24px',
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.sm,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      border: `2px solid ${theme.colors.primary}`,
    },
    
    planName: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    
    planPrice: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      color: theme.colors.primary,
      marginBottom: theme.spacing.lg,
    },
    
    planFeatures: {
      listStyle: 'none',
      padding: 0,
      marginBottom: theme.spacing.xl,
    },
    
    planFeature: {
      padding: '12px 0',
      color: theme.colors.textLight,
      borderBottom: `1px solid ${theme.colors.border}`,
      fontSize: theme.typography.fontSizes.base,
      textAlign: 'left',
    },
    
    planButton: {
      width: '100%',
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      padding: '12px 16px',
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    // Final CTA with Gradient
    finalCta: {
      backgroundColor: theme.colors.secondary,
      color: '#fff',
      padding: '100px 20px',
      textAlign: 'center',
      position: 'relative',
    },
    
    finalCtaContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    finalCtaTitle: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.bold,
      marginBottom: theme.spacing.lg,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    finalCtaText: {
      fontSize: theme.typography.fontSizes.xl,
      marginBottom: theme.spacing['2xl'],
      maxWidth: '600px',
      margin: `0 auto ${theme.spacing['2xl']} auto`,
      opacity: 0.9,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    finalCtaButton: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.md,
      padding: '20px 50px',
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    // Responsive adjustments
    '@media (maxWidth: 768px)': {
      heroTitle: {
        fontSize: theme.typography.fontSizes['4xl'],
      },
      heroSubtitle: {
        fontSize: theme.typography.fontSizes.xl,
      },
      featuresGrid: {
        gridTemplateColumns: '1fr',
      },
      heroButtons: {
        flexDirection: 'column',
        alignItems: 'center',
      },

    },
  };

  const handleButtonHover = (e, isHovering) => {
    if (isHovering) {
      e.target.style.transform = 'translateY(-2px) scale(1.05)';
    } else {
      e.target.style.transform = 'translateY(0) scale(1)';
    }
  };

  const handleCardHover = (e, isHovering) => {
    if (isHovering) {
      Object.assign(e.currentTarget.style, styles.featureCardHover);
    } else {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }
  };

  return (
    <div style={styles.container}>
      {/* Enhanced Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroPattern}></div>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            Create AI Chatbots<br />
            In Minutes, Not Months
          </h1>
          <p style={styles.heroSubtitle}>
            No coding. No AI experience. No hassle.<br/>
            Start converting visitors into customers 24/7.
          </p>
          <div style={styles.heroButtons}>
            <button 
              style={styles.primaryButton}
              onMouseEnter={(e) => handleButtonHover(e, true)}
              onMouseLeave={(e) => handleButtonHover(e, false)}
              onClick={onGetStarted}
            >
              [Start Free Today]
            </button>
          </div>
        </div>
      </section>

      {/* Enhanced Features */}
      <section style={styles.features}>
        <div style={styles.featuresContent}>
          <h2 style={styles.sectionTitle}>
            Why Businesses Choose ByteBot
            <div style={styles.sectionTitleUnderline}></div>
          </h2>
          <div style={styles.featuresGrid}>
            <div 
              style={styles.featureCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div style={styles.featureIcon}>[AI]</div>
              <h3 style={styles.featureTitle}>Quick Setup</h3>
              <p style={styles.featureDescription}>
                Create your AI chatbot in minutes using our guided setup process. 
                No coding or AI expertise required - just answer a few questions about your business.
              </p>
            </div>
            <div 
              style={styles.featureCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div style={styles.featureIcon}>[KB]</div>
              <h3 style={styles.featureTitle}>Custom Knowledge Base</h3>
              <p style={styles.featureDescription}>
                Train your chatbot with your business information, FAQs, and policies. 
                Your bot will provide accurate, consistent answers based on your content.
              </p>
            </div>
            <div 
              style={styles.featureCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div style={styles.featureIcon}>[24/7]</div>
              <h3 style={styles.featureTitle}>Always Available</h3>
              <p style={styles.featureDescription}>
                Your chatbot works around the clock, capturing leads and answering questions 
                even when you're sleeping. Never miss a potential customer again.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Stats */}
      <section style={styles.stats}>
        <div style={styles.statsOverlay}></div>
        <div style={styles.statsContent}>
          <h2 style={styles.statsTitle}>Built for Business Success</h2>
          <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>[24/7]</div>
              <div style={styles.statLabel}>Availability</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>[0]</div>
              <div style={styles.statLabel}>Setup Fees</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>[∞]</div>
              <div style={styles.statLabel}>Scalability</div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Pricing */}
      <section style={styles.pricing}>
        <div style={styles.pricingContent}>
          <h2 style={styles.sectionTitle}>
            Simple, Predictable Pricing
            <div style={styles.sectionTitleUnderline}></div>
          </h2>
          <div style={styles.pricingGrid}>
            <div style={styles.pricingCard}>
              <h3 style={styles.planName}>[Free]</h3>
              <div style={styles.planPrice}>$0<span style={{fontSize: theme.typography.fontSizes.lg, color: theme.colors.textLight}}>/mo</span></div>
              <ul style={styles.planFeatures}>
                <li style={styles.planFeature}>• 50 conversations/month</li>
                <li style={styles.planFeature}>• 1 chatbot</li>
                <li style={styles.planFeature}>• Widget customization</li>
                <li style={styles.planFeature}>• ByteBot branding</li>
              </ul>
              <button 
                style={styles.planButton} 
                onClick={() => handlePlanClick('Free')}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.colors.primaryDark;
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme.colors.primary;
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                [Get Started Free]
              </button>
            </div>
            
            <div style={{...styles.pricingCard, ...styles.popularCard}}>
              <div style={styles.popularBadge}>[Most Popular]</div>
              <h3 style={styles.planName}>[Premium]</h3>
              <div style={styles.planPrice}>$29<span style={{fontSize: theme.typography.fontSizes.lg, color: theme.colors.textLight}}>/mo</span></div>
              <ul style={styles.planFeatures}>
                <li style={styles.planFeature}>• Everything in Free, plus:</li>
                <li style={styles.planFeature}>• 5,000 conversations/month</li>
                <li style={styles.planFeature}>• Remove branding</li>
                <li style={styles.planFeature}>• Advanced analytics</li>
                <li style={styles.planFeature}>• Email support</li>
              </ul>
              <button 
                style={styles.planButton} 
                onClick={() => handlePlanClick('Premium')}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.colors.primaryDark;
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme.colors.primary;
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                [Start Premium]
              </button>
            </div>
            
            <div style={styles.pricingCard}>
              <h3 style={styles.planName}>[Growth]</h3>
              <div style={styles.planPrice}>$79<span style={{fontSize: theme.typography.fontSizes.lg, color: theme.colors.textLight}}>/mo</span></div>
              <ul style={styles.planFeatures}>
                <li style={styles.planFeature}>• Everything in Premium, plus:</li>
                <li style={styles.planFeature}>• 15,000 conversations/month</li>
                <li style={styles.planFeature}>• 5 chatbots</li>
                <li style={styles.planFeature}>• Priority support</li>
              </ul>
              <button 
                style={styles.planButton} 
                onClick={() => handlePlanClick('Growth')}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.colors.primaryDark;
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme.colors.primary;
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                [Start Growth]
              </button>
            </div>

            <div style={styles.pricingCard}>
              <h3 style={styles.planName}>[Agency]</h3>
              <div style={styles.planPrice}>$199<span style={{fontSize: theme.typography.fontSizes.lg, color: theme.colors.textLight}}>/mo</span></div>
              <ul style={styles.planFeatures}>
                <li style={styles.planFeature}>• Everything in Growth, plus:</li>
                <li style={styles.planFeature}>• 50,000 conversations/month</li>
                <li style={styles.planFeature}>• Unlimited chatbots</li>
                <li style={styles.planFeature}>• Dedicated support</li>
              </ul>
              <button 
                style={styles.planButton} 
                onClick={() => handlePlanClick('Agency')}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.colors.primaryDark;
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme.colors.primary;
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                [Start Agency]
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Final CTA */}
      <section style={styles.finalCta}>
        <div style={styles.finalCtaContent}>
          <h2 style={styles.finalCtaTitle}>Ready to Transform Your Customer Support?</h2>
          <p style={styles.finalCtaText}>
            Start building your AI chatbot today<br/>
            and never miss another customer inquiry.
          </p>
          <button 
            style={styles.finalCtaButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryDark;
              e.target.style.transform = 'translateY(-3px) scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.transform = 'translateY(0) scale(1)';
            }}
            onClick={onGetStarted}
          >
            [Get Started Free]
          </button>
        </div>
      </section>
    </div>
  );
}

export default Homepage; 